import { getEnv, UseCase } from '@smile-next/uikit';
import { ErrorBase } from '@/errors/ErrorBase';
import { Auth, cognitoAuth } from '@/infra/auth/Auth';
import { SignApi, signApi } from '@/infra/auth/SignApi';
import { AuthParamsRepository, authParamsRepository } from '@/infra/repository/AuthParamsRepository';
import { AuthenticatedFailurePayload } from '@/usecases/payload/AuthenticatedFailurePayload';
import { AuthenticatedSuccessPayload } from '@/usecases/payload/AuthenticatedSuccessPayload';

type VerifyAuthenticatedUserUsecaseInfraArgs = {
    authApi?: Auth;
    signApi?: SignApi;
    authParamsRepository?: AuthParamsRepository;
};

type VerifyAuthenticatedUserUsecaseArgs = VerifyAuthenticatedUserUsecaseInfraArgs & {
    skipFetchSignedCookie?: boolean;
};

export class VerifyAuthenticatedUserUseCase extends UseCase {
    private infra: Required<VerifyAuthenticatedUserUsecaseInfraArgs>;
    private skipFetchSignedCookie: boolean;

    constructor(args: VerifyAuthenticatedUserUsecaseArgs = {}) {
        super();
        this.infra = {
            authApi: cognitoAuth,
            signApi,
            authParamsRepository,
            ...args,
        };
        this.skipFetchSignedCookie = args.skipFetchSignedCookie != null || getEnv().env === 'local';
    }

    async execute() {
        // 保持情報のみで認証チェックを行う
        const authParams = this.infra.authParamsRepository.get();

        if (!authParams.hasCloudId()) {
            // 認証に必要な情報が足りない場合は失敗にし、不正な情報をクリアする
            this.infra.authParamsRepository.clear();
            return Promise.reject();
        }

        // 認証情報のチェックを行う
        try {
            await this.infra.authApi.checkCurrentUser();
            if (!this.skipFetchSignedCookie) {
                await this.infra.signApi.fetchSignedCookie();
            }

            // custom:availableServices属性がない場合は旧IDトークンのままなので、
            // AuthenticatedSuccessPayloadを発火してリダイレクトさせるとリダイレクトループに
            // なってしまうのでrejectしてログインをやり直させる。
            // https://jsport.aws.justsystems.com/bunkyo-next/smile-next-login/smile-next-login-frontend/-/issues/61
            if (!(await this.infra.authApi.hasAvailableServices())) {
                return Promise.reject();
            }

            this.dispatch(new AuthenticatedSuccessPayload());
        } catch (error) {
            if (error instanceof ErrorBase) {
                this.dispatch(
                    new AuthenticatedFailurePayload({
                        authParams,
                        error,
                    }),
                );
            }
            return Promise.reject();
        }

        return Promise.resolve();
    }
}
